import { CalendarIcon, ExclamationmarkTriangleFillIcon } from '@navikt/aksel-icons';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { BodyShort, HStack, VStack } from '@navikt/ds-react';

import {
    BrukerRolleSak_fpoversikt,
    MorsAktivitet,
    NavnPåForeldre,
    UttakPeriodeAnnenpartEøs_fpoversikt,
    UttakPeriode_fpoversikt,
} from '@navikt/fp-types';
import { Uttaksdagen, capitalizeFirstLetter, formatDateExtended } from '@navikt/fp-utils';
import { assertUnreachable } from '@navikt/fp-validation';

import { useUttaksplanData } from '../../../../context/UttaksplanDataContext';
import { Uttaksplanperiode, erEøsUttakPeriode, erVanligUttakPeriode } from '../../../../types/UttaksplanPeriode';
import { getVarighetString } from '../../../../utils/dateUtils';
import { erAvslåttPeriode, harPeriodeDerMorsAktivitetIkkeErValgt, harPeriodeMedUkjentGraderingsaktivitet } from '../../../../utils/periodeUtils';
import { getStønadskvoteNavn } from '../../../utils/uttaksplanListeUtils';

interface Props {
    periode: UttakPeriode_fpoversikt | UttakPeriodeAnnenpartEøs_fpoversikt;
    inneholderKunEnPeriode: boolean;
    navnPåForeldre: NavnPåForeldre;
    erFarEllerMedmor: boolean;
}

export const UttaksperiodeContent = ({ periode, inneholderKunEnPeriode, navnPåForeldre, erFarEllerMedmor }: Props) => {
    const intl = useIntl();
    const {
        foreldreInfo: { rettighetType, søker, erIkkeSøkerSpesifisert, erFarOgFar },
        uttakPerioder,
        kanVelgeArbeidsgiver,
    } = useUttaksplanData();
    const erAvslått = erAvslåttPeriode(periode);
    const morsAktivitet = erVanligUttakPeriode(periode) && periode.morsAktivitet ? periode.morsAktivitet : undefined;

    const stønadskvoteNavn = getStønadskvoteNavn(intl, {
        navnPåForeldre,
        erFarEllerMedmor,
        erEøsPeriode: erEøsUttakPeriode(periode),
        morsAktivitet,
        konto: periode.kontoType,
        erAleneOmOmsorg: rettighetType === 'ALENEOMSORG',
        erAvslått,
    });

    const morsPerioder = uttakPerioder.filter(
        (p): p is UttakPeriode_fpoversikt => !erEøsUttakPeriode(p) && p.forelder === 'MOR',
    );

    return (
        <HStack gap="space-8">
            {harPeriodeDerMorsAktivitetIkkeErValgt(
                rettighetType,
                søker,
                erIkkeSøkerSpesifisert ?? false,
                [periode, ...morsPerioder],
                erFarOgFar,
            ) && (
                <ExclamationmarkTriangleFillIcon
                    title={intl.formatMessage({ id: 'PeriodeListeHeader.MorsAktivitetIkkeValgt' })}
                    fontSize="1.5rem"
                    className="text-ax-danger-800"
                />
            )}
            {kanVelgeArbeidsgiver && harPeriodeMedUkjentGraderingsaktivitet([periode], søker) && (
                <ExclamationmarkTriangleFillIcon
                    title={intl.formatMessage({ id: 'PeriodeListeHeader.GraderingsaktivitetIkkeValgt' })}
                    fontSize="1.5rem"
                    className="text-ax-danger-800"
                />
            )}
            <div>
                <CalendarIcon width={24} height={24} />
            </div>
            <VStack gap="space-4">
                <HStack gap="space-8">
                    <BodyShort weight="semibold">{getLengdePåPeriode(intl, inneholderKunEnPeriode, periode)}</BodyShort>
                    <BodyShort>
                        {getVarighetString(
                            Uttaksdagen.denneEllerNeste(periode.fom).getUttaksdagerFremTilOgMedDato(periode.tom),
                            intl,
                        )}
                    </BodyShort>
                </HStack>
                <VStack gap="space-8">
                    <BodyShort>{stønadskvoteNavn}</BodyShort>
                    {!erAvslått && morsAktivitet !== undefined && (
                        <BodyShort>{getMorsAktivitetTekst(intl, morsAktivitet)}</BodyShort>
                    )}
                    {erEøsUttakPeriode(periode) && periode.trekkdager !== undefined ? (
                        <BodyShort>
                            <FormattedMessage id="uttaksplan.periodeListeContent.eøs" />
                        </BodyShort>
                    ) : (
                        <BodyShort>
                            {getTekstForArbeidOgSamtidigUttak(periode, erFarEllerMedmor, navnPåForeldre)}
                        </BodyShort>
                    )}
                </VStack>
            </VStack>
        </HStack>
    );
};

const getLengdePåPeriode = (intl: IntlShape, inneholderKunEnPeriode: boolean, periode: Uttaksplanperiode) => {
    if (inneholderKunEnPeriode) {
        return intl.formatMessage({ id: 'uttaksplan.varighet.helePerioden' });
    }

    return `${formatDateExtended(periode.fom)} - ${formatDateExtended(periode.tom)}`;
};

export const getMorsAktivitetTekst = (intl: IntlShape, aktivitet: MorsAktivitet): string => {
    switch (aktivitet) {
        case 'ARBEID':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.Arbeid' });
        case 'ARBEID_OG_UTDANNING':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.ArbeidOgUtdanning' });
        case 'INNLAGT':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.Innlagt' });
        case 'INTROPROG':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.Introduksjonsprogrammet' });
        case 'KVALPROG':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.Kvalifiseringsprogrammet' });
        case 'TRENGER_HJELP':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.TrengerHjelp' });
        case 'UFØRE':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.Uføre' });
        case 'UTDANNING':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.Utdanning' });
        case 'IKKE_OPPGITT':
            return intl.formatMessage({ id: 'uttaksplan.periodeListeContent.morsAktivitet.UtenAktivitetsKrav' });
        default:
            return assertUnreachable('Mor har ingen aktivitet');
    }
};

const getTekstForArbeidOgSamtidigUttak = (
    periode: Uttaksplanperiode,
    erFarEllerMedmor: boolean,
    navnPåForeldre: NavnPåForeldre,
) => {
    if (erVanligUttakPeriode(periode)) {
        if (
            (periode.gradering !== undefined && periode.samtidigUttak !== undefined) ||
            periode.gradering !== undefined
        ) {
            const uttaksprosent = Math.round((100 - periode.gradering.arbeidstidprosent) * 100) / 100;
            return (
                <FormattedMessage
                    id="uttaksplan.periodeListeContent.arbeid"
                    values={{ arbeidstidprosent: periode.gradering.arbeidstidprosent, uttaksprosent }}
                />
            );
        }

        if (periode.samtidigUttak !== undefined) {
            return getSamtidigUttakTekst(periode.samtidigUttak, periode.forelder, erFarEllerMedmor, navnPåForeldre);
        }
    }

    return undefined;
};

const getSamtidigUttakTekst = (
    samtidiguttaksProsent: number,
    forelderIPerioden: BrukerRolleSak_fpoversikt,
    erFarEllerMedmor: boolean,
    navnPåForeldre: NavnPåForeldre,
) => {
    const periodenGjelderSøker = erFarEllerMedmor ? forelderIPerioden === 'FAR_MEDMOR' : forelderIPerioden === 'MOR';
    const navnPåAnnenForelderIPerioden = erFarEllerMedmor ? navnPåForeldre.mor : navnPåForeldre.farMedmor;
    const navnPåHovedforelderIPerioden = erFarEllerMedmor ? navnPåForeldre.farMedmor : navnPåForeldre.mor;

    return periodenGjelderSøker ? (
        <FormattedMessage
            id="uttaksplan.periodeListeContent.samtidigUttak"
            values={{
                samtidiguttaksProsent,
                navnPåHovedforelderIPerioden: capitalizeFirstLetter(navnPåHovedforelderIPerioden),
            }}
        />
    ) : (
        <FormattedMessage
            id="uttaksplan.periodeListeContent.samtidigUttak.annenForelder"
            values={{
                navnPåAnnenForelderIPerioden: capitalizeFirstLetter(navnPåAnnenForelderIPerioden),
                samtidiguttaksProsent,
            }}
        />
    );
};
